'use client';

import { Airwave } from '@crabs-ai/web-icons';
import { ContentDialog } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';

import './ripple-edit-guide-animation.css';

const TRACK_SCENE_CONTAINER =
  'absolute left-1/2 top-[calc(50%+13.4px)] flex w-[184px] -translate-x-1/2 -translate-y-1/2 flex-col gap-[2.8px]';
const TRACK_ROW = 'flex h-[28px] items-center gap-[2.8px]';

function CursorIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden
    >
      <path
        d="M4 1L4 15L8.5 10.5L13 18L15.5 16.5L11 9L17 9L4 1Z"
        fill="var(--sys-cont-none-light)"
        stroke="var(--sys-cont-neutral-default)"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function RippleBadge({ on }: { on: boolean }) {
  return (
    <div
      className={[
        'absolute top-[22px] z-10 flex items-center rounded-3xs py-4xs pr-2xs pl-4xs',
        on
          ? 'left-1/2 -translate-x-1/2 bg-ref-extended-blue-700'
          : 'left-1/2 -translate-x-1/2 bg-ref-neutral-700',
      ].join(' ')}
    >
      <Airwave className="size-4 brightness-0 invert" />
      <span className="text-comp-accent-xsmall text-sys-cont-none-light">
        {on ? 'ON' : 'OFF'}
      </span>
    </div>
  );
}

function RippleOffAnimation() {
  return (
    <div className="relative h-[140px] w-full overflow-hidden rounded-sm bg-sys-bg-surface-2">
      <RippleBadge on={false} />
      <div className={TRACK_SCENE_CONTAINER}>
        <div className={TRACK_ROW}>
          <div className="h-full w-[59px] shrink-0 rounded-[5.6px] bg-sys-style-primary-default" />
          <div className="animate-ripple-delete h-full w-[53px] shrink-0 rounded-[5.6px] border-2 border-sys-cont-inverse-dark bg-sys-style-primary-default" />
          <div className="h-full flex-1 rounded-[5.6px] bg-sys-style-primary-default" />
        </div>
        <div className={TRACK_ROW}>
          <div className="h-full w-[59px] shrink-0 rounded-[5.6px] bg-comp-label-green-bg" />
          <div className="animate-ripple-delete h-full w-[53px] shrink-0 rounded-[5.6px] border-2 border-sys-cont-inverse-dark bg-comp-label-green-bg" />
          <div className="h-full flex-1 rounded-[5.6px] bg-comp-label-green-bg" />
        </div>
      </div>

      <div className="animate-ripple-cursor absolute top-[68px] left-[132px] z-20">
        <CursorIcon />
      </div>
    </div>
  );
}

function RippleOnAnimation() {
  return (
    <div className="relative h-[140px] w-full overflow-hidden rounded-sm bg-sys-bg-surface-2">
      <RippleBadge on />
      <div className={TRACK_SCENE_CONTAINER}>
        <div className={TRACK_ROW}>
          <div className="h-full w-[59px] shrink-0 rounded-[5.6px] bg-sys-style-primary-default" />
          <div className="animate-ripple-delete h-full w-[53px] shrink-0 rounded-[5.6px] border-2 border-sys-cont-inverse-dark bg-sys-style-primary-default" />
          <div className="animate-ripple-close h-full flex-1 rounded-[5.6px] bg-sys-style-primary-default" />
        </div>
        <div className={TRACK_ROW}>
          <div className="h-full w-[59px] shrink-0 rounded-[5.6px] bg-comp-label-green-bg" />
          <div className="animate-ripple-delete h-full w-[53px] shrink-0 rounded-[5.6px] border-2 border-sys-cont-inverse-dark bg-comp-label-green-bg" />
          <div className="animate-ripple-close-delay h-full flex-1 rounded-[5.6px] bg-comp-label-green-bg" />
        </div>
      </div>

      <div className="animate-ripple-cursor absolute top-[68px] left-[132px] z-20">
        <CursorIcon />
      </div>
    </div>
  );
}

interface RippleEditGuideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RippleEditGuideDialog({
  open,
  onOpenChange,
}: RippleEditGuideDialogProps) {
  const t = useTranslations();

  return (
    <ContentDialog
      open={open}
      setOpen={onOpenChange}
      titleNode={t('Editor.timeline.ripple-edit-guide-title')}
      descriptionNode={t('Editor.timeline.ripple-edit-guide-description')}
      isShowCancelButton={false}
      confirmText={t('Editor.timeline.ripple-edit-guide-confirm')}
      onConfirm={() => onOpenChange(false)}
      contentClassName="w-[600px] max-w-[calc(100vw-48px)]"
    >
      <div className="grid grid-cols-2 gap-2xs">
        <div className="flex flex-col gap-2xs">
          <RippleOffAnimation />
          <div className="flex flex-col gap-4xs">
            <p className="text-caption-medium text-sys-cont-neutral-default">
              {t('Editor.timeline.ripple-edit-guide-off-title')}
            </p>
            <p className="text-body-small text-sys-cont-neutral-lightest">
              {t('Editor.timeline.ripple-edit-guide-off-desc')}
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2xs">
          <RippleOnAnimation />
          <div className="flex flex-col gap-4xs">
            <p className="text-caption-medium text-sys-cont-neutral-default">
              {t('Editor.timeline.ripple-edit-guide-on-title')}
            </p>
            <p className="text-body-small text-sys-cont-neutral-lightest">
              {t('Editor.timeline.ripple-edit-guide-on-desc')}
            </p>
          </div>
        </div>
      </div>
    </ContentDialog>
  );
}
